import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import QuoteOptionsCard from "@/components/QuoteOptionsCard";
import { useAuth } from "@/context/AuthContext";
import { OWNER_EMAIL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Building2, Save, Upload, Plus, Trash2, Loader2, Landmark, Lock, FileText, Download, FolderOpen, Quote, Pin, PinOff, ThumbsUp, Heart, Timer, ListOrdered, ArrowRight, AlertTriangle, Mail } from "lucide-react";

const BANK_FIELDS = [
  ["label", "Column Label"], ["account_name", "Account Name"],
  ["account_tzs", "Account No. (TZS)"], ["account_usd", "Account No. (USD)"],
  ["bank_name", "Bank Name"], ["branch", "Branch"],
  ["branch_code", "Branch Code"], ["swift", "Swift Code"], ["sort_code", "Sort Code"],
];
const DOC_CATEGORIES = ["Business License", "Certificate", "Company Profile", "Compliance", "Tax", "Insurance", "Other"];
const CAT_TINT = {
  "Business License": "bg-[#E6ECF5] text-[#1B4383]", Certificate: "bg-emerald-100 text-emerald-700",
  "Company Profile": "bg-violet-100 text-violet-700", Compliance: "bg-amber-100 text-amber-700",
  Tax: "bg-sky-100 text-sky-700", Insurance: "bg-rose-100 text-rose-700", Other: "bg-slate-100 text-slate-600",
};
const fmtSize = (b) => (!b ? "—" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

const Field = ({ label, value, onChange, testid, placeholder, disabled }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} disabled={disabled} />
  </div>
);

export default function Settings() {
  const { user } = useAuth();
  const isOwner = (user?.email || "").toLowerCase() === OWNER_EMAIL;

  // --- Company profile ---
  const [cp, setCp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // --- Documents ---
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Other", file: null });
  const [delTarget, setDelTarget] = useState(null);
  // --- Daily quotes (owner only) ---
  const [quotes, setQuotes] = useState([]);
  const [newQuote, setNewQuote] = useState("");
  const [newQuoteAuthor, setNewQuoteAuthor] = useState("");
  const [qDelTarget, setQDelTarget] = useState(null);
  // --- Pipeline rules (owner editable) ---
  const [rules, setRules] = useState(null);
  const [savingRules, setSavingRules] = useState(false);
  // --- Renumbering (owner only) ---
  const [rnEntities, setRnEntities] = useState([]);
  const [rnEntity, setRnEntity] = useState("order");
  const [rnYear, setRnYear] = useState("");
  const [rnPreview, setRnPreview] = useState(null);
  const [rnLoading, setRnLoading] = useState(false);
  const [rnApplying, setRnApplying] = useState(false);
  const [rnConfirmText, setRnConfirmText] = useState("");

  useEffect(() => {
    api.get("/settings/company").then(({ data }) => { setCp(data); setLoading(false); })
      .catch((e) => { toast.error(apiError(e)); setLoading(false); });
    loadDocs();
    api.get("/motivational-quotes").then(({ data }) => setQuotes(data)).catch(() => {});
    api.get("/settings/pipeline").then(({ data }) => setRules(data)).catch(() => {});
    api.get("/renumber/entities").then(({ data }) => setRnEntities(data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addQuote = async () => {
    const text = newQuote.trim();
    if (!text) { toast.error("Enter a quote"); return; }
    try {
      const { data } = await api.post("/motivational-quotes", { text, author: newQuoteAuthor.trim() });
      setQuotes((q) => [...q, data]); setNewQuote(""); setNewQuoteAuthor(""); toast.success("Quote added");
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeQuote = async () => {
    const q = qDelTarget; setQDelTarget(null);
    try {
      await api.delete(`/motivational-quotes/${q.id}`);
      setQuotes((list) => list.filter((x) => x.id !== q.id)); toast.success("Quote removed");
    } catch (e) { toast.error(apiError(e)); }
  };
  const pinQuote = async (q) => {
    try {
      await api.post(`/motivational-quotes/${q.id}/pin`);
      setQuotes((list) => list.map((x) => ({ ...x, pinned: x.id === q.id })));
      toast.success("Pinned — this quote now shows on every Dashboard");
    } catch (e) { toast.error(apiError(e)); }
  };
  const unpinQuotes = async () => {
    try {
      await api.post("/motivational-quotes/unpin");
      setQuotes((list) => list.map((x) => ({ ...x, pinned: false })));
      toast.success("Back to daily rotation");
    } catch (e) { toast.error(apiError(e)); }
  };
  const [qSel, setQSel] = useState(new Set());
  const toggleQSel = (id) => setQSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkRemoveQuotes = async () => {
    const ids = [...qSel];
    if (!ids.length) return;
    if (!window.confirm(`Remove ${ids.length} quote${ids.length !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    try {
      await Promise.all(ids.map((id) => api.delete(`/motivational-quotes/${id}`)));
      setQuotes((list) => list.filter((x) => !qSel.has(x.id)));
      setQSel(new Set()); toast.success(`Removed ${ids.length} quote${ids.length !== 1 ? "s" : ""}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const pinnedQuote = quotes.find((q) => q.pinned);
  const reactQuote = async (q, type) => {
    try {
      const { data } = await api.post(`/motivational-quotes/${q.id}/react`, { type });
      setQuotes((list) => list.map((x) => (x.id === q.id ? { ...x, reactions: data.reactions, my_reactions: data.my_reactions } : x)));
    } catch (e) { toast.error(apiError(e)); }
  };
  const mostLoved = quotes.reduce((a, q) => ((q.reactions?.love || 0) > (a?.reactions?.love || 0) ? q : a), null);
  const mostLovedId = mostLoved && (mostLoved.reactions?.love || 0) > 0 ? mostLoved.id : null;

  const loadDocs = () => {
    setDocsLoading(true);
    api.get("/settings/documents").then(({ data }) => { setDocs(data); setDocsLoading(false); })
      .catch((e) => { toast.error(apiError(e)); setDocsLoading(false); });
  };

  const set = (patch) => setCp((c) => ({ ...c, ...patch }));
  const setBank = (i, k, v) => setCp((c) => ({ ...c, banks: c.banks.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)) }));
  const addBank = () => setCp((c) => ({ ...c, banks: [...(c.banks || []), { label: `BANK ${(c.banks || []).length + 1}` }] }));
  const removeBank = (i) => setCp((c) => ({ ...c, banks: c.banks.filter((_, idx) => idx !== i) }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Logo must be under 1.5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => set({ logo_b64: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", cp);
      setCp(data);
      toast.success("Company profile saved — it now feeds every PDF export");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const readFileB64 = (file) => new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });

  const uploadDoc = async () => {
    if (!form.file) { toast.error("Choose a file to upload"); return; }
    if (form.file.size > 25 * 1024 * 1024) { toast.error("File must be under 25 MB"); return; }
    setUploading(true);
    try {
      const b64 = await readFileB64(form.file);
      const { data } = await api.post("/settings/documents", {
        title: form.title.trim() || form.file.name.replace(/\.[^.]+$/, ""),
        category: form.category, filename: form.file.name,
        content_type: form.file.type || "application/octet-stream", file_base64: b64,
      });
      setDocs((d) => [data, ...d]);
      setForm({ title: "", category: "Other", file: null });
      toast.success("Document added to the repository");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/settings/documents/${d.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: d.content_type || "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const confirmDelete = async () => {
    const d = delTarget; setDelTarget(null);
    try {
      await api.delete(`/settings/documents/${d.id}`);
      setDocs((list) => list.filter((x) => x.id !== d.id));
      toast.success("Document removed");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      const { data } = await api.put("/settings/pipeline", {
        auto_lost_enabled: rules.auto_lost_enabled,
        auto_lost_days: Number(rules.auto_lost_days) || 30,
      });
      setRules(data);
      toast.success("Pipeline rule saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingRules(false); }
  };

  const rnCur = rnEntities.find((e) => e.key === rnEntity) || null;
  const rnYears = rnCur?.years || [];

  const selectEntity = (key) => {
    setRnEntity(key);
    setRnPreview(null);
    const ent = rnEntities.find((e) => e.key === key);
    const yrs = ent?.years || [];
    setRnYear(ent?.year_scoped && yrs.length ? String(yrs[0].year) : "");
  };

  const loadRnPreview = async () => {
    setRnLoading(true);
    setRnPreview(null);
    try {
      const params = { entity: rnEntity };
      if (rnCur?.year_scoped && rnYear) params.year = rnYear;
      const { data } = await api.get("/renumber/preview", { params });
      setRnPreview(data);
      if (data.total === 0) toast.info("No records found for this selection.");
    } catch (e) { toast.error(apiError(e)); } finally { setRnLoading(false); }
  };

  const applyRenumber = async () => {
    setRnApplying(true);
    try {
      const body = { entity: rnEntity };
      if (rnCur?.year_scoped && rnYear) body.year = Number(rnYear);
      const { data } = await api.post("/renumber/apply", body);
      toast.success(data.message || `Renumbered ${data.changed} record(s)`);
      setRnConfirmText("");
      const { data: ents } = await api.get("/renumber/entities");
      setRnEntities(ents);
      await loadRnPreview();
    } catch (e) { toast.error(apiError(e)); } finally { setRnApplying(false); }
  };

  const actions = isOwner ? (
    <Button onClick={save} disabled={saving || loading} data-testid="settings-save" className="bg-[#1B4383] hover:bg-[#16386e]">
      {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save Profile
    </Button>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-500" data-testid="settings-readonly-chip"><Lock className="h-3.5 w-3.5" /> View only</span>
  );

  return (
    <AppLayout title="Company Information" actions={actions}>
      <Tabs defaultValue="profile" className="max-w-4xl" data-testid="settings-page">
        <TabsList className="mb-6 flex h-auto w-full justify-start overflow-x-auto sm:inline-flex sm:w-auto">
          <TabsTrigger value="profile" data-testid="tab-profile"><Building2 className="mr-1.5 h-4 w-4" />Company Profile</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FolderOpen className="mr-1.5 h-4 w-4" />Documents</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes"><Quote className="mr-1.5 h-4 w-4" />Daily Quotes</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline"><Timer className="mr-1.5 h-4 w-4" />Pipeline Rules</TabsTrigger>
          <TabsTrigger value="quote-options" data-testid="tab-quote-options"><ListOrdered className="mr-1.5 h-4 w-4" />Quote Options</TabsTrigger>
          {isOwner && <TabsTrigger value="renumber" data-testid="tab-renumber"><ListOrdered className="mr-1.5 h-4 w-4" />Renumbering</TabsTrigger>}
          <TabsTrigger value="outlook" data-testid="tab-outlook"><Mail className="mr-1.5 h-4 w-4" />Outlook Add-In</TabsTrigger>
        </TabsList>

        {/* ---------------- PROFILE ---------------- */}
        <TabsContent value="outlook" className="space-y-4" data-testid="outlook-addin-settings">
          <div className="max-w-2xl rounded-xl border border-border bg-white p-4 sm:p-6">
            <h3 className="font-heading text-base font-bold text-slate-800">Add To Somcore Hub — Outlook Button</h3>
            <p className="mt-1 text-sm text-slate-600">Adds An <b>Add To Somcore Hub</b> Button To Every Email In Outlook (Web, New Outlook For Windows / Mac). One Click Reads The Email And Its PDF And Opens A Pre-Filled New RFQ.</p>
            <Button className="mt-4 bg-[#1B4383] hover:bg-[#16386e]" data-testid="outlook-manifest-download" onClick={async () => {
              try {
                const res = await api.get("/outlook/manifest.xml", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
                const a = document.createElement("a"); a.href = url; a.download = "somcore-hub-outlook.xml"; a.click();
                setTimeout(() => window.URL.revokeObjectURL(url), 30000);
              } catch (e) { toast.error(apiError(e)); }
            }}><Download className="mr-2 h-4 w-4" />Download Add-In File</Button>
            <ol className="mt-4 list-decimal space-y-1 pl-5 text-sm text-slate-700">
              <li>Download The Add-In File Above <b>From The Live Published Site</b> (Not The Preview).</li>
              <li>In Outlook, Go To <b>Get Add-Ins</b> → <b>My Add-Ins</b> → <b>Add A Custom Add-In</b> → <b>Add From File</b>, And Pick The File.</li>
              <li>Open Any Email → Click <b>Add To Somcore Hub</b> (In The Ribbon Or The … Menu) → Sign In Once → <b>Create RFQ From This Email</b>.</li>
            </ol>
            <p className="mt-3 text-xs text-muted-foreground">For The Whole Team, Your Microsoft 365 Admin Can Upload The Same File Under Integrated Apps.</p>
          </div>
        </TabsContent>
        <TabsContent value="profile" className="space-y-6">
          {loading || !cp ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">These details print on every generated document — the Somcore client quote and supplier RFQ PDFs.</p>
              {!isOwner && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="settings-readonly-banner">
                  <Lock className="h-4 w-4 shrink-0" /> Only the account owner can edit the company profile. You can view the current details below.
                </div>
              )}
              <div className="rounded-xl border border-border bg-white p-4 sm:p-6">
                <div className="mb-4 flex items-center gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#E6ECF5] text-[#1B4383]"><Building2 className="h-4 w-4" /></span>
                  <h3 className="font-heading text-sm font-bold text-slate-800">Company Details</h3>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Company Name" value={cp.name} onChange={(v) => set({ name: v })} testid="cp-name" disabled={!isOwner} />
                  <Field label="Email" value={cp.email} onChange={(v) => set({ email: v })} testid="cp-email" disabled={!isOwner} />
                  <Field label="Phone" value={cp.phone} onChange={(v) => set({ phone: v })} testid="cp-phone" disabled={!isOwner} />
                  <Field label="Address" value={cp.address} onChange={(v) => set({ address: v })} testid="cp-address" disabled={!isOwner} />
                  <Field label="TIN" value={cp.tin} onChange={(v) => set({ tin: v })} testid="cp-tin" disabled={!isOwner} />
                  <Field label="VRN" value={cp.vrn} onChange={(v) => set({ vrn: v })} testid="cp-vrn" disabled={!isOwner} />
                </div>
                <div className="mt-5 border-t border-border pt-4">
                  <Label className="text-xs text-slate-600">Logo (prints on PDFs)</Label>
                  <div className="mt-2 flex flex-wrap items-center gap-3 sm:gap-4">
                    <div className="flex h-16 w-40 items-center justify-center overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50">
                      {cp.logo_b64 ? <img src={cp.logo_b64} alt="logo" className="max-h-full max-w-full object-contain" data-testid="cp-logo-preview" />
                        : <img src="/somcore-logo.png" alt="default logo" className="max-h-full max-w-full object-contain opacity-80" />}
                    </div>
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 aria-disabled:pointer-events-none aria-disabled:opacity-50" aria-disabled={!isOwner} data-testid="cp-logo-upload">
                      <Upload className="h-4 w-4" /> Upload logo
                      <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onLogo} disabled={!isOwner} />
                    </label>
                    {cp.logo_b64 && isOwner && <Button variant="ghost" size="sm" onClick={() => set({ logo_b64: "" })} data-testid="cp-logo-reset" className="text-slate-500">Reset to default</Button>}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-white p-4 sm:p-6">
                <div className="mb-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#E4EEE8] text-[#527D63]"><Landmark className="h-4 w-4" /></span>
                    <h3 className="font-heading text-sm font-bold text-slate-800">Bank Details</h3>
                  </div>
                  <Button variant="outline" size="sm" onClick={addBank} data-testid="cp-add-bank" disabled={!isOwner}><Plus className="mr-1.5 h-4 w-4" />Add bank</Button>
                </div>
                <div className="space-y-4">
                  {(cp.banks || []).map((b, i) => (
                    <div key={i} className="rounded-lg border border-border bg-slate-50/40 p-3 sm:p-4" data-testid={`cp-bank-${i}`}>
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{b.label || `Bank ${i + 1}`}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeBank(i)} className="text-red-600 hover:bg-red-50" data-testid={`cp-remove-bank-${i}`} disabled={!isOwner}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {BANK_FIELDS.map(([k, lbl]) => (
                          <div key={k} className={["account_name", "bank_name"].includes(k) ? "col-span-2 sm:col-span-1" : ""}><Field label={lbl} value={b[k]} onChange={(v) => setBank(i, k, v)} testid={`cp-bank-${i}-${k}`} disabled={!isOwner} /></div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!(cp.banks || []).length && <p className="text-sm text-muted-foreground">No banks added. The quote PDF will omit the bank section.</p>}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* ---------------- DOCUMENTS ---------------- */}
        <TabsContent value="documents" className="space-y-6">
          <p className="text-sm text-muted-foreground">Central repository for important company documents — everyone can view &amp; download; only the owner can add or remove.</p>

          {isOwner && (
            <div className="rounded-xl border border-border bg-white p-4 sm:p-6" data-testid="doc-upload-card">
              <div className="mb-4 flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#E6ECF5] text-[#1B4383]"><Upload className="h-4 w-4" /></span>
                <h3 className="font-heading text-sm font-bold text-slate-800">Add a Document</h3>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5 sm:col-span-1">
                  <Label className="text-xs text-slate-600">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Business License 2026" data-testid="doc-title" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">File</Label>
                  <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} data-testid="doc-file" />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={uploadDoc} disabled={uploading} data-testid="doc-upload-btn" className="bg-[#1B4383] hover:bg-[#16386e]">
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Upload
                </Button>
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-border bg-white" data-testid="doc-list">
            {docsLoading ? (
              <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading documents…</div>
            ) : !docs.length ? (
              <div className="p-10 text-center text-sm text-muted-foreground">No documents yet.</div>
            ) : (
              <>
                {/* Desktop table */}
                <table className="hidden w-full text-sm sm:table">
                  <thead>
                    <tr className="border-b border-border bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                      <th className="px-4 py-3 font-medium">Document</th>
                      <th className="px-4 py-3 font-medium">Category</th>
                      <th className="px-4 py-3 font-medium">Size</th>
                      <th className="px-4 py-3 font-medium">Uploaded</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((d) => (
                      <tr key={d.id} className="border-b border-border last:border-0 hover:bg-slate-50/60" data-testid={`doc-row-${d.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-500"><FileText className="h-4 w-4" /></span>
                            <div className="min-w-0">
                              <div className="truncate font-medium text-slate-800">{d.title}</div>
                              <div className="truncate text-xs text-muted-foreground">{d.filename}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${CAT_TINT[d.category] || CAT_TINT.Other}`}>{d.category}</span></td>
                        <td className="px-4 py-3 text-muted-foreground">{fmtSize(d.size)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{fmtDate(d.created_at)}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)} data-testid={`doc-download-${d.id}`} className="text-[#1B4383]"><Download className="mr-1.5 h-4 w-4" />Open</Button>
                            {isOwner && <Button variant="ghost" size="sm" onClick={() => setDelTarget(d)} data-testid={`doc-delete-${d.id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile cards */}
                <div className="divide-y divide-border sm:hidden">
                  {docs.map((d) => (
                    <div key={d.id} className="p-4" data-testid={`doc-card-${d.id}`}>
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500"><FileText className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <div className="break-words font-medium text-slate-800">{d.title}</div>
                          <div className="break-words text-xs text-muted-foreground">{d.filename}</div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${CAT_TINT[d.category] || CAT_TINT.Other}`}>{d.category}</span>
                            <span className="text-xs text-muted-foreground">{fmtSize(d.size)} · {fmtDate(d.created_at)}</span>
                          </div>
                          {d.uploaded_by_name ? <div className="mt-1 text-xs text-muted-foreground">By {d.uploaded_by_name}</div> : null}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => downloadDoc(d)} data-testid={`doc-download-m-${d.id}`} className="flex-1 text-[#1B4383]"><Download className="mr-1.5 h-4 w-4" />Open</Button>
                        {isOwner && <Button variant="ghost" size="sm" onClick={() => setDelTarget(d)} data-testid={`doc-delete-m-${d.id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="mr-1.5 h-4 w-4" />Remove</Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="quotes" className="space-y-6">
          <p className="text-sm text-muted-foreground">These quotes rotate daily on everyone's Dashboard — one shows per day. Anyone on the team can add a quote (with an author) and pin one for the day so the rest of us see it. Pinning overrides the rotation until it's unpinned.</p>
          {pinnedQuote && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#C9D8EC] bg-[#EEF3FA] px-4 py-2.5 text-sm text-[#1B4383]" data-testid="quote-pinned-banner">
              <span className="flex items-center gap-2"><Pin className="h-4 w-4 shrink-0" /> Overriding the rotation with: "{pinnedQuote.text}"</span>
              <Button variant="outline" size="sm" onClick={unpinQuotes} data-testid="quote-unpin-btn"><PinOff className="mr-1.5 h-4 w-4" />Back to Daily Rotation</Button>
            </div>
          )}
          <div className="rounded-xl border border-border bg-white p-4 sm:p-6" data-testid="quote-add-card">
            <Label className="text-xs text-slate-600">Add a Quote</Label>
            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_200px_auto]">
              <Input value={newQuote} onChange={(e) => setNewQuote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addQuote()} placeholder="Quote — e.g. Deliver more than you promise." data-testid="quote-input" />
              <Input value={newQuoteAuthor} onChange={(e) => setNewQuoteAuthor(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addQuote()} placeholder="Author (optional)" data-testid="quote-author-input" />
              <Button onClick={addQuote} data-testid="quote-add-btn" className="bg-[#1B4383] hover:bg-[#16386e]"><Plus className="mr-1.5 h-4 w-4" />Add</Button>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-border bg-white" data-testid="quote-list">
            {!quotes.length ? (
              <div className="p-10 text-center text-sm text-muted-foreground">No quotes yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {quotes.map((q) => (
                  <div key={q.id} className={`flex items-start gap-3 px-4 py-3 ${q.pinned ? "bg-[#EEF3FA]" : ""}`} data-testid={`quote-row-${q.id}`}>
                    {isOwner && <Checkbox checked={qSel.has(q.id)} onCheckedChange={() => toggleQSel(q.id)} className="mt-1" aria-label="Select quote" data-testid={`quote-select-${q.id}`} />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm italic text-slate-700">"{q.text}"{q.pinned && <span className="ml-2 rounded-full bg-[#1B4383] px-2 py-0.5 text-[10px] font-semibold not-italic text-white">Showing today</span>}</p>
                      <div className="mt-0.5 text-xs text-muted-foreground">{q.author ? `— ${q.author}` : "— Unattributed"}{q.added_by_name ? ` · added by ${q.added_by_name}` : ""}</div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <button onClick={() => reactQuote(q, "like")} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${q.my_reactions?.includes("like") ? "border-[#1B4383] bg-[#EEF3FA] text-[#1B4383]" : "border-border text-slate-500 hover:bg-slate-50"}`} data-testid={`quote-like-${q.id}`}><ThumbsUp className="h-3 w-3" />{q.reactions?.like || 0}</button>
                        <button onClick={() => reactQuote(q, "love")} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${q.my_reactions?.includes("love") ? "border-[#C24E4E] bg-[#F9ECEC] text-[#C24E4E]" : "border-border text-slate-500 hover:bg-slate-50"}`} data-testid={`quote-love-${q.id}`}><Heart className="h-3 w-3" />{q.reactions?.love || 0}</button>
                        {mostLovedId === q.id && <span className="rounded-full bg-[#F9ECEC] px-2 py-0.5 text-[10px] font-semibold text-[#C24E4E]">Most loved</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {q.pinned ? (
                        <Button variant="ghost" size="sm" onClick={unpinQuotes} className="text-[#1B4383]" title="Unpin (return to rotation)" data-testid={`quote-unpin-${q.id}`}><PinOff className="h-4 w-4" /></Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => pinQuote(q)} className="text-slate-500 hover:text-[#1B4383]" title="Pin to today's Dashboard" data-testid={`quote-pin-${q.id}`}><Pin className="h-4 w-4" /></Button>
                      )}
                      {(isOwner || q.added_by_email === user?.email) && (
                        <Button variant="ghost" size="sm" onClick={() => setQDelTarget(q)} className="text-red-600 hover:bg-red-50" data-testid={`quote-delete-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {isOwner && qSel.size > 0 && (
            <div className="flex items-center justify-between rounded-lg border border-border bg-white px-4 py-2.5" data-testid="quote-bulk-bar">
              <span className="text-sm text-slate-600">{qSel.size} selected</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setQSel(new Set())}>Clear</Button>
                <Button size="sm" onClick={bulkRemoveQuotes} className="bg-red-600 hover:bg-red-700" data-testid="quote-bulk-remove"><Trash2 className="mr-1.5 h-4 w-4" />Remove Selected</Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ---------------- PIPELINE RULES ---------------- */}
        <TabsContent value="quote-options" className="space-y-6">
          <QuoteOptionsCard isOwner={isOwner} />
        </TabsContent>
        <TabsContent value="pipeline" className="space-y-6">
          <p className="text-sm text-muted-foreground">Automatic pipeline housekeeping — these rules run once a day.</p>
          {!isOwner && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="pipeline-readonly-banner">
              <Lock className="h-4 w-4 shrink-0" /> Only the account owner can change pipeline rules. You can view them below.
            </div>
          )}
          {!rules ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="max-w-xl rounded-xl border border-border bg-white p-4 sm:p-6" data-testid="pipeline-rules-card">
              <div className="mb-4 flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#F4E7E7] text-[#9D4848]"><Timer className="h-4 w-4" /></span>
                <h3 className="font-heading text-sm font-bold text-slate-800">Auto-mark stale quotes as Lost</h3>
              </div>
              <label className="flex items-start gap-3">
                <Checkbox checked={!!rules.auto_lost_enabled} onCheckedChange={(v) => setRules((r) => ({ ...r, auto_lost_enabled: !!v }))} disabled={!isOwner} className="mt-0.5" data-testid="pipeline-auto-lost-toggle" />
                <span className="text-sm text-slate-700">When a quote sits at the <b>Quoted</b> stage with no movement, automatically move it to <b>Lost</b>. A reason is stamped so you can tell auto-lost from manually-lost deals.</span>
              </label>
              <div className="mt-5 flex items-center gap-3">
                <Label className="text-xs text-slate-600">Days at Quoted before Lost</Label>
                <Input type="number" min={1} max={365} value={rules.auto_lost_days ?? 30} onChange={(e) => setRules((r) => ({ ...r, auto_lost_days: e.target.value }))} disabled={!isOwner || !rules.auto_lost_enabled} className="w-24" data-testid="pipeline-auto-lost-days" />
              </div>
              {isOwner && (
                <div className="mt-6 flex justify-end">
                  <Button onClick={saveRules} disabled={savingRules} data-testid="pipeline-save" className="bg-[#1B4383] hover:bg-[#16386e]">
                    {savingRules ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save Rule
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ---------------- RENUMBERING (owner only) ---------------- */}
        {isOwner && (
        <TabsContent value="renumber" className="space-y-5">
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="renumber-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Document numbers normally stay <b>permanent</b> once issued. This management override compacts a sequence so codes run 0001…N with no gaps and re-orders them on a business date. Linked records (orders, shipments, quotes) are updated automatically. <b>Finance Hub numbering is never touched.</b> Use with care.</span>
          </div>

          <div className="max-w-3xl rounded-xl border border-border bg-white p-4 sm:p-6" data-testid="renumber-card">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Document type</Label>
                <Select value={rnEntity} onValueChange={selectEntity}>
                  <SelectTrigger className="w-[220px]" data-testid="renumber-entity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {rnEntities.map((e) => (
                      <SelectItem key={e.key} value={e.key} data-testid={`renumber-entity-${e.key}`}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {rnCur?.year_scoped && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Year</Label>
                  <Select value={rnYear} onValueChange={(v) => { setRnYear(v); setRnPreview(null); }}>
                    <SelectTrigger className="w-[120px]" data-testid="renumber-year"><SelectValue placeholder="Year" /></SelectTrigger>
                    <SelectContent>
                      {rnYears.map((y) => (
                        <SelectItem key={y.year} value={String(y.year)} data-testid={`renumber-year-${y.year}`}>{y.year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button variant="outline" onClick={loadRnPreview} disabled={rnLoading || (rnCur?.year_scoped && !rnYear)} data-testid="renumber-preview-btn">
                {rnLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListOrdered className="mr-2 h-4 w-4" />}Preview
              </Button>
            </div>
            {rnCur && (
              <p className="mt-3 text-xs text-muted-foreground">Ordered by <b>{rnCur.basis_label}</b>. {rnCur.year_scoped ? "Sequence restarts each year." : "Single running sequence."}</p>
            )}
          </div>

          {rnPreview && rnPreview.total > 0 && (
            <div className="max-w-3xl rounded-xl border border-border bg-white" data-testid="renumber-preview">
              <div className="flex items-center justify-between border-b border-border px-5 py-3">
                <div className="text-sm">
                  <span className="font-semibold text-slate-800">{rnPreview.label}</span>
                  <span className="text-muted-foreground"> · {rnPreview.total} records · </span>
                  <span className={rnPreview.changed ? "font-semibold text-[#9D4848]" : "text-emerald-600 font-medium"}>
                    {rnPreview.changed ? `${rnPreview.changed} will change` : "already sequential"}
                  </span>
                </div>
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                    <tr>
                      <th className="px-5 py-2 font-medium">Current</th>
                      <th className="px-2 py-2 font-medium">New</th>
                      <th className="px-5 py-2 font-medium">{rnPreview.basis_label}</th>
                      <th className="px-5 py-2 font-medium">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rnPreview.rows.map((r) => (
                      <tr key={r.id} className={`border-t border-border/60 ${r.changed ? "bg-amber-50/40" : ""}`} data-testid={`renumber-row-${r.id}`}>
                        <td className="px-5 py-1.5 font-mono text-xs text-slate-600">{r.old_code}</td>
                        <td className="px-2 py-1.5">
                          {r.changed ? (
                            <span className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-[#1B4383]"><ArrowRight className="h-3 w-3" />{r.new_code}</span>
                          ) : <span className="text-xs text-slate-300">—</span>}
                        </td>
                        <td className="px-5 py-1.5 text-xs text-slate-500">{r.basis_date || "—"}</td>
                        <td className="px-5 py-1.5 text-xs text-slate-600 truncate max-w-[220px]">{r.label || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rnPreview.changed > 0 && (
                <div className="flex flex-col gap-3 border-t border-border px-5 py-4 sm:flex-row sm:items-end sm:justify-between">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Type <b>RENUMBER</b> to confirm</Label>
                    <Input value={rnConfirmText} onChange={(e) => setRnConfirmText(e.target.value)} placeholder="RENUMBER" className="w-48" data-testid="renumber-confirm-input" />
                  </div>
                  <Button onClick={applyRenumber} disabled={rnApplying || rnConfirmText.trim().toUpperCase() !== "RENUMBER"} className="bg-[#9D4848] hover:bg-[#853c3c]" data-testid="renumber-apply-btn">
                    {rnApplying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListOrdered className="mr-2 h-4 w-4" />}Apply Renumbering
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>
        )}
      </Tabs>

        <AlertDialog open={!!qDelTarget} onOpenChange={(o) => !o && setQDelTarget(null)}>
          <AlertDialogContent data-testid="quote-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove this quote?</AlertDialogTitle>
              <AlertDialogDescription>"{qDelTarget?.text}" will no longer appear on the Dashboard.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={removeQuote} className="bg-red-600 hover:bg-red-700" data-testid="quote-delete-confirm">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="doc-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this document?</AlertDialogTitle>
            <AlertDialogDescription>"{delTarget?.title}" will be removed from the repository.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="doc-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
